/**
 * AiGenerateButton — small inline "Generar con IA" trigger.
 *
 * Drop next to any SEO/marketing field. Builds the context via getContext(),
 * calls the AI endpoint for `task`, and hands the result back through
 * onResult so the parent can fill the field. Shows a spinner while running
 * and surfaces quota / engine errors via toast.
 *
 * Only renders when the user holds AI_USE — otherwise nothing shows, so the
 * button doesn't tease a feature the user can't use.
 */

import { useState } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { aiApi, type AiTask } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { PERMISSIONS } from '@/types';

interface AiGenerateButtonProps {
  task: AiTask;
  /** Build the text the model works from. Return '' to abort with a hint. */
  getContext: () => string;
  onResult: (text: string) => void;
  language?: 'es' | 'en';
  label?: string;
  /** Hint shown when getContext() returns empty. */
  emptyHint?: string;
}

export default function AiGenerateButton({
  task,
  getContext,
  onResult,
  language = 'es',
  label = 'Generar con IA',
  emptyHint = 'Rellena primero el título o la descripción.',
}: AiGenerateButtonProps) {
  const { hasPermission } = useAuthStore();
  const [loading, setLoading] = useState(false);

  if (!hasPermission(PERMISSIONS.AI_USE)) return null;

  const handleClick = async () => {
    const context = getContext().trim();
    if (!context) {
      toast.error(emptyHint);
      return;
    }
    setLoading(true);
    try {
      const res = await aiApi.generate(task, context, language);
      const result: string = res.data.result || '';
      if (!result) {
        toast.error('La IA no devolvió contenido. Inténtalo de nuevo.');
        return;
      }
      onResult(result);
      const remaining = res.data.usage?.remaining;
      toast.success(
        typeof remaining === 'number'
          ? `Generado con IA · te quedan ${remaining} hoy`
          : 'Generado con IA'
      );
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (status === 429) {
        toast.error(msg || 'Límite diario de IA alcanzado.');
      } else {
        toast.error(msg || 'Error al generar con IA');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className="inline-flex items-center gap-1 text-xs font-medium text-vida-600 hover:text-vida-700 disabled:opacity-50 disabled:cursor-wait"
      title="Generar este campo con el asistente de IA local"
    >
      <SparklesIcon className="w-3.5 h-3.5" />
      {loading ? 'Generando…' : label}
    </button>
  );
}
